import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import {
    ArrowDownTrayIcon,
    ArrowTopRightOnSquareIcon,
    CameraIcon,
    DocumentMagnifyingGlassIcon,
    EllipsisHorizontalIcon,
    InformationCircleIcon,
    NoSymbolIcon,
    UserIcon
} from '@heroicons/react/24/outline'
import mixpanel from 'mixpanel-browser'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import {
    ContentView,
    EnrichedContent,
    Screenshot,
    ScreenshotStatus,
    useCaptureScreenshotMutation,
    useSetManualShadowBanForAccountMutation
} from '../../../api/client'
import { getSocialMediaContentLink, getSocialMediaPostLink } from './ContentLink'
import { downloadMetadataAsText } from './metadata/formatMetadataAsText'
import Spinner from '../../Spinner'
import {
    canManageShadowBanningState,
    canViewScreenshotsState,
    isUserAdminState
} from '../../../store/DomainStore'
import { toastError, toastSuccess } from '../../Notification'

type Props = {
    content: ContentView
    enrichedContent: EnrichedContent | undefined
    onViewDetails: () => void
    onShadowBanChange?: (contentId: number, profileId: number) => void
    isShadowBanned: boolean
    screenshot?: Screenshot | null
    onScreenshotRefetch?: () => void
}

/**
 * Component rendering a "more actions" menu for content items, including shadow banning.
 *
 * When we go responsive some of the content card actions will be in this menu.
 *
 * Other actions that don't belong on the card can go here too.
 */
export default function ContentMoreActions(props: Props) {
    const { content, enrichedContent, onViewDetails, onShadowBanChange, isShadowBanned, screenshot, onScreenshotRefetch } = props
    const [setManualShadowBanForAccount] = useSetManualShadowBanForAccountMutation()
    const [captureScreenshot, { loading: captureScreenshotLoading }] = useCaptureScreenshotMutation()
    const [canManageShadowBanning] = useRecoilState(canManageShadowBanningState)
    const [canViewScreenshots] = useRecoilState(canViewScreenshotsState)
    const canUseShadowBanning = canManageShadowBanning && content.account?.shadowBanningEnabled
    const [admin] = useRecoilState(isUserAdminState)

    // Screenshots are only supported for Instagram, Facebook, and Twitter
    const screenshotSupportedPlatforms = ['instagram', 'facebook', 'twitter']
    const isScreenshotSupported = screenshotSupportedPlatforms.includes(
        content.socialMediaServiceName?.toLowerCase() ?? ''
    )

    // The Post / Comment links are hidden on narrow cards (see ContentLink), so the
    // menu is where they stay reachable. Same resolution as ContentLink.
    const postLink = content.socialMediaPostLink || getSocialMediaPostLink(enrichedContent, content)
    const commentLink =
        content.socialMediaContentLink || getSocialMediaContentLink(enrichedContent, content)

    const getShadowBanTooltip = () => {
        if (!canManageShadowBanning) {
            return 'Contact support@arwen.ai to enable shadow banning'
        }
        if (!content.account?.shadowBanningEnabled) {
            return 'Shadow banning is not enabled for this account'
        }
        return undefined
    }

    const handleShadowBanClick = async () => {
        const accountId = content.account?.id
        const profileId = content.authorProfileId ?? null
        const contentId = content.id

        if (!accountId || !contentId) {
            toastError('Unable to update shadow ban: missing account or content information')
            return
        }

        mixpanel.track('Content - shadow ban clicked', {
            accountId,
            contentId: content.id,
            profileId,
            action: isShadowBanned ? 'remove' : 'add'
        })

        try {
            const result = await setManualShadowBanForAccount({
                variables: {
                    accountId,
                    profileId,
                    contentId,
                    enabled: !isShadowBanned
                }
            })
            const returnedProfileId = result.data?.setManualShadowBanForAccount.profileId
            toastSuccess(isShadowBanned ? 'Shadow ban removed' : 'User has been shadow banned')
            if (returnedProfileId) {
                onShadowBanChange?.(contentId, returnedProfileId)
            }
        } catch (error) {
            console.error(error)
            toastError(
                isShadowBanned
                    ? 'Failed to remove shadow ban. Please try again.'
                    : 'Failed to shadow ban user. Please try again.'
            )
        }
    }

    const handleInfoClick = () => {
        mixpanel.track('Content - info button clicked', {
            accountId: content.account?.id,
            contentId: content.id
        })
        onViewDetails()
    }

    const handleCaptureScreenshot = async () => {
        try {
            const result = await captureScreenshot({
                variables: { contentId: content.id }
            })
            const attempt = result.data?.captureScreenshot
            if (attempt?.s3Url) {
                window.open(attempt.s3Url, '_blank')
            }
            toastSuccess('Screenshot requested')
            // Refetch to update menu with new screenshot status
            onScreenshotRefetch?.()
        } catch (error) {
            console.error(error)
            toastError('Failed to request screenshot')
        }
    }

    const handleDownloadMetadata = () => {
        mixpanel.track('Content - download metadata clicked', {
            accountId: content.account?.id,
            contentId: content.id
        })
        downloadMetadataAsText({ content, enrichedContent })
    }

    return (
    <>
        <Menu as="div" className="relative pl-1">
            <MenuButton
                data-tooltip-id="tooltip-id"
                data-tooltip-content="Click for more comment actions"
                className="w-6 h-6 p-0 transition-colors duration-200 ease-in-out text-gray-700 hover:text-gray-950 active:scale-95"
                type="button"
            >
                <div className="relative">
                    <EllipsisHorizontalIcon />
                </div>
            </MenuButton>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <MenuItems className="absolute left-0 z-10 w-56 origin-top-right rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {content.authorProfileId && content.account?.id && (
                        <MenuItem>
                            {({ focus }) => (
                                <Link
                                    to={`/author/${content.authorProfileId}/account/${content.account?.id}`}
                                    className={`${
                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } flex w-full items-center px-4 py-2 transition-colors duration-100`}
                                >
                                    <UserIcon className="mr-3 h-5 w-5" />
                                    View all comments by this author
                                </Link>
                            )}
                        </MenuItem>
                    )}
                    <MenuItem>
                        {({ focus }) => (
                            <button
                                className={`${
                                    focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } flex w-full items-center px-4 py-2 ${!canUseShadowBanning ? 'opacity-50 cursor-not-allowed' : ''}`}
                                type="button"
                                onClick={canUseShadowBanning ? handleShadowBanClick : undefined}
                                disabled={!canUseShadowBanning}
                                data-tooltip-id="tooltip-id"
                                data-tooltip-content={getShadowBanTooltip()}
                            >
                                <NoSymbolIcon className="mr-3 h-5 w-5" />
                                {isShadowBanned ? 'Remove shadow ban' : 'Shadow ban this author'}
                            </button>
                        )}
                    </MenuItem>

                    {/* Original post and comment on the platform */}
                    {postLink && (
                        <MenuItem>
                            {({ focus }) => (
                                <a
                                    href={postLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => mixpanel.track('Content - post link clicked', { link: postLink })}
                                    className={`${
                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } flex w-full items-center px-4 py-2 transition-colors duration-100`}
                                >
                                    <ArrowTopRightOnSquareIcon className="mr-3 h-5 w-5" />
                                    Open post
                                </a>
                            )}
                        </MenuItem>
                    )}
                    {commentLink && (
                        <MenuItem>
                            {({ focus }) => (
                                <a
                                    href={commentLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => mixpanel.track('Content - comment link clicked', { link: commentLink })}
                                    className={`${
                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } flex w-full items-center px-4 py-2 transition-colors duration-100`}
                                >
                                    <ArrowTopRightOnSquareIcon className="mr-3 h-5 w-5" />
                                    Open comment
                                </a>
                            )}
                        </MenuItem>
                    )}

                    {/* Metadata section */}
                    <div className="my-1 border-t border-gray-200" />
                    <MenuItem>
                        {({ focus }) => (
                            <button
                                className={`${
                                    focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } flex w-full items-center px-4 py-2 transition-colors duration-100`}
                                type="button"
                                onClick={handleInfoClick}
                            >
                                <InformationCircleIcon className="mr-3 h-5 w-5" />
                                View metadata
                            </button>
                        )}
                    </MenuItem>
                    <MenuItem>
                        {({ focus }) => (
                            <button
                                className={`${
                                    focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } flex w-full items-center px-4 py-2 transition-colors duration-100`}
                                type="button"
                                onClick={handleDownloadMetadata}
                            >
                                <ArrowDownTrayIcon className="mr-3 h-5 w-5" />
                                Download metadata
                            </button>
                        )}
                    </MenuItem>
                    {admin && (
                        <MenuItem>
                            {({ focus }) => (
                                <Link
                                    to={`/content/${content.id}`}
                                    className={`${
                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } flex w-full items-center px-4 py-2`}
                                >
                                    <DocumentMagnifyingGlassIcon className="mr-3 h-5 w-5" />
                                    Full details
                                </Link>
                            )}
                        </MenuItem>
                    )}
                    <div className="my-1 border-t border-gray-200" />

                    {/* Screenshot section - show for supported platforms with screenshots enabled */}
                    {(canViewScreenshots || admin) && content.account.toggles?.enableScreenshots && isScreenshotSupported && (
                        <>
                            {screenshot?.status === ScreenshotStatus.Success && screenshot.s3Url ? (
                                <MenuItem>
                                    {({ focus }) => (
                                        <a
                                            href={screenshot.s3Url ?? undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${
                                                focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                            } flex w-full items-center px-4 py-2 transition-colors duration-100`}
                                        >
                                            <CameraIcon className="mr-3 h-5 w-5" />
                                            <span className="flex items-center gap-2">
                                                View screenshot
                                                <i className="fas fa-external-link-alt text-xs" />
                                            </span>
                                        </a>
                                    )}
                                </MenuItem>
                            ) : screenshot?.status === ScreenshotStatus.Failed ? (
                                <MenuItem disabled>
                                    {() => (
                                        <div className="flex w-full items-center px-4 py-2 text-yellow-700">
                                            <InformationCircleIcon className="mr-3 h-5 w-5 flex-shrink-0 text-yellow-500" />
                                            <span className="text-sm">Screenshot not available</span>
                                        </div>
                                    )}
                                </MenuItem>
                            ) : screenshot?.status === ScreenshotStatus.Pending ? (
                                <MenuItem disabled>
                                    {({ focus }) => (
                                        <div
                                            className={`${
                                                focus ? 'bg-gray-100' : ''
                                            } flex w-full items-center px-4 py-2 text-gray-500`}
                                        >
                                            <CameraIcon className="mr-3 h-5 w-5" />
                                            <span>Screenshot in progress...</span>
                                        </div>
                                    )}
                                </MenuItem>
                            ) : (
                                <MenuItem disabled>
                                    {({ focus }) => (
                                        <div
                                            className={`${
                                                focus ? 'bg-gray-100' : ''
                                            } flex w-full items-center px-4 py-2 text-gray-500`}
                                        >
                                            <CameraIcon className="mr-3 h-5 w-5" />
                                            <span>Screenshot not taken</span>
                                        </div>
                                    )}
                                </MenuItem>
                            )}
                        </>
                    )}
                    {admin && isScreenshotSupported && (
                        <MenuItem>
                            {({ focus }) => (
                                <button
                                    className={`${
                                        focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } flex w-full items-center px-4 py-2 ${captureScreenshotLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    type="button"
                                    onClick={captureScreenshotLoading ? undefined : handleCaptureScreenshot}
                                    disabled={captureScreenshotLoading}
                                >
                                    {captureScreenshotLoading ? (
                                        <Spinner size={5} />
                                    ) : (
                                        <CameraIcon className="mr-3 h-5 w-5" />
                                    )}
                                    <span className={captureScreenshotLoading ? 'ml-3' : ''}>
                                        {captureScreenshotLoading ? 'Taking screenshot...' : 'Take screenshot'}
                                    </span>
                                </button>
                            )}
                        </MenuItem>
                    )}

                </MenuItems>
            </Transition>
        </Menu>

    </>
    )
}
